import React from 'react';
import { ErrorBoundary } from '@/components/error/ErrorBoundary';
import { useErrorTracking } from '@/hooks/useErrorTracking';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface AssessmentErrorBoundaryProps {
  children: React.ReactNode;
  onRetry?: () => void;
}

export function AssessmentErrorBoundary({
  children,
  onRetry
}: AssessmentErrorBoundaryProps) {
  const { captureError } = useErrorTracking();
  const { user } = useAuth();

  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    captureError(error, {
      user: user ? {
        id: user.id,
        email: user.email
      } : undefined,
      extra: {
        errorInfo,
        context: 'assessment_flow',
        timestamp: new Date().toISOString()
      },
      tags: {
        component: 'AssessmentErrorBoundary',
        level: 'page'
      }
    });
  };

  const AssessmentErrorFallback = () => (
    <div className="flex items-center justify-center min-h-[400px] p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">Assessment Error</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            Something went wrong with the assessment. Don't worry - your progress has been saved.
          </p>
          <div className="flex flex-col gap-2">
            {onRetry && (
              <Button onClick={onRetry} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => window.location.reload()}
              className="w-full"
            >
              Reload Page
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <ErrorBoundary
      level="page"
      fallback={<AssessmentErrorFallback />}
      onError={handleError}
      enableRetry={true}
      showContactSupport={true}
    >
      {children}
    </ErrorBoundary>
  );
}